import { http } from '@/lib/api-client'
import type {
  WhatsappSession,
  CreateSessionInput,
  ConnectResult,
  WhatsappMessage,
  WhatsappMessageListResponse,
  SendMessageInput,
  ListMessagesParams,
  CheckNumberResult,
} from './whatsapp.types'

// WhatsApp sessions — /api/whatsapp (proxies WasenderAPI). Responses are the
// WasenderAPI `{ success, data }` body; we unwrap `data`.
export const whatsappApi = {
  listSessions: (): Promise<WhatsappSession[]> =>
    http.get<{ data: WhatsappSession[] }>('/whatsapp/sessions').then((r) => r.data),

  getSession: (id: number): Promise<WhatsappSession> =>
    http.get<{ data: WhatsappSession }>(`/whatsapp/sessions/${id}`).then((r) => r.data),

  createSession: (data: CreateSessionInput): Promise<WhatsappSession> =>
    http.post<{ data: WhatsappSession }>('/whatsapp/sessions', data).then((r) => r.data),

  updateSession: (id: number, data: CreateSessionInput): Promise<WhatsappSession> =>
    http
      .put<{ data: WhatsappSession }>(`/whatsapp/sessions/${id}`, data)
      .then((r) => r.data),

  deleteSession: (id: number): Promise<void> => http.delete(`/whatsapp/sessions/${id}`),

  connect: (id: number): Promise<ConnectResult> =>
    http.post<{ data: ConnectResult }>(`/whatsapp/sessions/${id}/connect`).then((r) => r.data),

  qrCode: (id: number): Promise<{ qrCode: string }> =>
    http.get<{ data: { qrCode: string } }>(`/whatsapp/sessions/${id}/qrcode`).then((r) => r.data),

  disconnect: (id: number): Promise<void> =>
    http.post(`/whatsapp/sessions/${id}/disconnect`),

  // Messages — sent THROUGH a session; the audit log reads branch-wide.
  sendMessage: (
    sessionId: string,
    data: SendMessageInput,
  ): Promise<WhatsappMessage> =>
    http
      .post<{ data: WhatsappMessage }>(
        `/whatsapp/sessions/${sessionId}/messages`,
        data,
      )
      .then((r) => r.data),

  listMessages: (params?: ListMessagesParams): Promise<WhatsappMessageListResponse> =>
    http.get<WhatsappMessageListResponse>('/whatsapp/messages', { params }),

  // Verify a number is registered on WhatsApp (uses the session's api_key).
  checkNumber: (
    sessionId: string,
    phone: string,
  ): Promise<CheckNumberResult | null> =>
    http
      .get<{ data: CheckNumberResult | null }>(
        `/whatsapp/sessions/${sessionId}/numbers/${encodeURIComponent(phone)}`,
      )
      .then((r) => r.data),
}
